import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { get } from "@/lib/api";
import { formatDate, formatDateTime, formatNumber } from "@/lib/format";
import { ErrorState, TableSkeleton } from "@/components/common/DataStates";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

export default function PlatformUsersPage() {
  const [search, setSearch] = useState("");
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["platform-admin", "users"],
    queryFn: () => get("/admin/users"),
    retry: false,
  });
  const users = Array.isArray(data) ? data : [];
  const query = search.trim().toLocaleLowerCase();
  const visible = users.filter(({ full_name, email }) => [full_name, email].some((value) => String(value ?? "").toLocaleLowerCase().includes(query)));

  return (
    <div className="min-w-0 space-y-6" data-testid="platform-users">
      <div>
        <p className="og-section-label">Utilisateurs</p>
        <h2 className="mt-1 font-heading text-2xl font-semibold">Tous les utilisateurs</h2>
        <p className="mt-1 text-sm text-muted-foreground">Vue globale · lecture seule</p>
      </div>
      <section className="og-card min-w-0 overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-4">
          <label className="relative block w-full max-w-sm">
            <span className="sr-only">Rechercher un utilisateur</span>
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
            <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Rechercher un nom ou un email" className="pl-9" aria-label="Rechercher un utilisateur" />
          </label>
          {!isLoading && !isError && <span className="text-sm text-muted-foreground" aria-live="polite">{formatNumber(visible.length)} utilisateur{visible.length !== 1 ? "s" : ""}</span>}
        </div>
        {isLoading ? <TableSkeleton testId="platform-users-loading" />
          : isError ? <div className="p-4"><ErrorState message="Impossible de charger les utilisateurs." onRetry={refetch} /></div>
            : users.length === 0 ? <p className="p-6 text-sm text-muted-foreground">Aucun utilisateur enregistré.</p>
              : visible.length === 0 ? <p className="p-6 text-sm text-muted-foreground">Aucun utilisateur ne correspond à cette recherche.</p>
                : <div className="max-w-full overflow-x-auto" role="region" aria-label="Liste des utilisateurs" tabIndex={0}>
                  <table className="w-full min-w-[960px] text-left text-sm">
                    <thead className="bg-muted/50 text-xs text-muted-foreground"><tr>
                      {["Utilisateur", "Email", "Statut", "Admin plateforme", "Organisations", "Langue", "Créé le", "Dernière connexion"].map((label) => <th key={label} scope="col" className="whitespace-nowrap px-4 py-3 font-medium">{label}</th>)}
                    </tr></thead>
                    <tbody className="divide-y divide-border">{visible.map((user) => (
                      <tr key={user.id} className="hover:bg-muted/30">
                        <th scope="row" className="break-words px-4 py-3 font-medium">{user.full_name}</th>
                        <td className="break-all px-4 py-3">{user.email}</td>
                        <td className="px-4 py-3"><Badge variant="outline">{user.is_active ? "Actif" : "Inactif"}</Badge></td>
                        <td className="px-4 py-3">{user.is_platform_admin ? "Oui" : "Non"}</td>
                        <td className="px-4 py-3 tabular-nums">{formatNumber(user.organizations)}</td>
                        <td className="px-4 py-3">{user.locale || "—"}</td>
                        <td className="whitespace-nowrap px-4 py-3">{formatDate(user.created_at)}</td>
                        <td className="whitespace-nowrap px-4 py-3">{formatDateTime(user.last_login_at)}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>}
      </section>
    </div>
  );
}